import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2, Users, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FUERTE = (p) => p.length >= 8 && /[A-Z]/.test(p) && /[a-z]/.test(p) && /\d/.test(p);

function Shell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-sky-50 to-[#E0F2FE]/60 px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl border border-sky-100 shadow-xl shadow-sky-100/60 p-8" data-testid="invitacion-view">
        <div className="flex items-center gap-2.5 mb-6">
          <img src="/nuvlipro-logo.png" alt="NuvliPro" className="w-9 h-9 object-contain" />
          <span className="font-heading font-extrabold text-[#0A2540] text-lg">NuvliPro</span>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function AceptarInvitacion() {
  const { token } = useParams();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/equipo/invitacion/${encodeURIComponent(token)}`)
      .then((r) => { setInv(r.data); setForm((f) => ({ ...f, name: r.data.nombre || "" })); })
      .catch((e) => { setInv(false); setError(formatApiErrorDetail(e?.response?.data?.detail) || "La invitación no es válida."); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!FUERTE(form.password)) { toast.error("La contraseña debe tener mínimo 8 caracteres, con mayúscula, minúscula y número"); return; }
    if (form.password !== form.confirm) { toast.error("Las contraseñas no coinciden"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/equipo/invitacion/aceptar", { token, name: form.name.trim(), password: form.password });
      setUser(data);
      toast.success(`Bienvenido al equipo de ${inv.empresa}`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "No se pudo aceptar la invitación");
      setLoading(false);
    }
  };

  if (inv === null) return <Shell><div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#0284C7]" /></div></Shell>;
  if (inv === false) {
    return (
      <Shell>
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900" data-testid="invitacion-error">
          <AlertTriangle className="w-5 h-5 shrink-0" /> {error}
        </div>
        <Link to="/login" className="block text-center text-sm text-[#0284C7] mt-6 hover:underline" data-testid="invitacion-login-link">Ir a iniciar sesión</Link>
      </Shell>
    );
  }
  return (
    <Shell>
      <h1 className="font-heading text-2xl font-extrabold text-[#0A2540] mb-2 flex items-center gap-2"><Users className="w-6 h-6 text-[#0284C7]" /> Únete al equipo</h1>
      <p className="text-sm text-slate-600 mb-6" data-testid="invitacion-texto">
        <strong>{inv.titular}</strong> te ha invitado a colaborar en <strong>{inv.empresa}</strong>. Crea tu contraseña para entrar con <strong>{inv.email}</strong>.
      </p>
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Tu nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="invitacion-nombre" /></div>
        <div>
          <Label>Contraseña</Label>
          <Input type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" data-testid="invitacion-password" />
          <p className="text-xs text-slate-400 mt-1">Mínimo 8 caracteres, con mayúscula, minúscula y número.</p>
        </div>
        <div><Label>Repite la contraseña</Label><Input type="password" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="mt-1.5" data-testid="invitacion-confirm" /></div>
        <Button type="submit" disabled={loading || !form.name.trim() || !form.password} className="w-full h-11 bg-[#0A2540] hover:bg-[#031B33] text-white" data-testid="invitacion-aceptar-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aceptar invitación y entrar"}
        </Button>
      </form>
    </Shell>
  );
}
